import { headers, logPrefix } from './constants';
import JSONResponseHandler from '../serviceHelpers/JSONResponseHandler';
import SingletonFetch from '../serviceHelpers/SingletonFetch';
import { isUserIdentity, UserIdentity } from './userIdentity';
import { APIErrorResponse } from '../serviceHelpers/APIErrorResponse';
import Identity from './identity';
import { isJwtExpired, jwtHasExp } from '../utils/jwt';

/**
 * Extends a session using a refresh token which is received when calling the `login` method with the `rememberMe` parameter as `true`.  You can find this token in `userIdentity`.  The `refreshToken` can be re-used multiple times to acquire new `accessToken`s (default up to 1 year).
 *
 * @param refreshToken
 *
 * @returns
 * A __Promise__ with user info data. When successful, the accessToken in `userIdentity` will be updated.
 *
 * <details>
 * <summary>
 * On success (resolves)
 * </summary>
 *
 * ```json
 * {
 *     "uuid": "c7e9fed4-bedf-428b-a694-1bbd21721cf7",
 *     "accessToken": "c7e9fed4bedf428b.a6941bbd21721cf7..."
 * }
 * ```
 * </details>
 *
 * <details>
 * <summary>
 * On error (throws)
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#/Authentication/authToken
 *
 * ```
 * </details>
 *
 */

export function extendSession(): Promise<UserIdentity | APIErrorResponse> {
  if (
    Identity.userIdentity.refreshToken &&
    Identity.userIdentity.accessToken &&
    !isJwtExpired(Identity.userIdentity.refreshToken)
  ) {
    return SingletonFetch(
      `${Identity.apiOrigin}/identity/public/v1/auth/token`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          token: Identity.userIdentity.refreshToken,
          grantType: 'refresh-token'
        })
      },
      'extendSession'
    )
      .then(JSONResponseHandler)
      .then(json => {
        if (isUserIdentity(json)) {
          const userIdentity = {
            ...Identity.userIdentity,
            accessToken: json.accessToken,
            refreshToken: json.refreshToken || Identity.userIdentity.refreshToken
          };

          Identity.userIdentity = userIdentity;
          return Identity.userIdentity;
        } else {
          if (json.code) {
            console.warn(`${logPrefix} code:"${json.code}", message:"${json.message}"`);
          }
          Identity.clearSession();
          throw json;
        }
      });
  } else if (
    Identity.userIdentity.refreshToken &&
    isJwtExpired(Identity.userIdentity.refreshToken)
  ) {
    Identity.clearSession();
    throw new Error(`${logPrefix} Refresh token is expired`);
  } else {
    Identity.clearSession();
    throw new Error(`${logPrefix} No refresh token found for this session`);
  }
}

/**
 * This method calls extendSession using the current `Identity.userIdentity.refreshToken` (if one is available)
 *
 * @returns {(Promise<APIErrorResponse | UserIdentity>)}
 */
export function heartbeat(): Promise<APIErrorResponse | UserIdentity> {
  if (
    Identity.userIdentity.refreshToken &&
    Identity.userIdentity.accessToken &&
    (isJwtExpired(Identity.userIdentity.accessToken) ||
      !jwtHasExp(Identity.userIdentity.accessToken))
  ) {
    return Identity.extendSession();
  } else {
    return Promise.resolve(Identity.userIdentity);
  }
}
